import { ELASTIC_SEARCH_MAXIMUM_PAGE } from 'constants/pagination'
import { DEFAULT_PRODUCT_SORTING_ID } from 'constants/sorting'
import { NotFoundError } from 'helpers/errors'
import { getValidGender } from 'helpers/gender'
import { redirect } from 'helpers/navigation'
import { FetchProps } from 'helpers/redial'
import { getSortChannel } from 'helpers/sort'
import { transaction } from 'mobx'
import { getAvailableFiltersWithAvailableAndSelectedValues } from 'pages/Category/helpers/getAvailableFiltersWithAvailableAndSelectedValues'
import { FilterItem } from 'services/providers/bapi/entities/filters/types'
import { createProductStreamFetcher } from 'sharedComponents/ProductStream/loadingHelpers'
import { SelectedLooseFilterMapType } from 'store/sidecar/FilterViewStore'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'

export default {
  fetch: async ({ store, service, query, pathname }: FetchProps) => {
    const sortId = DEFAULT_PRODUCT_SORTING_ID
    const currentPage = query.page ? parseInt(query.page, 10) : 1

    store.searchPage.startLoading()
    let seoStreamPageData
    try {
      seoStreamPageData = await service.seoStreamPage.requestBySlug(
        pathname.split('/')[2],
      )
    } catch (e) {
      throw new NotFoundError('Not found')
    }
    store.seoStreamPage.setSeoStreamPageData(seoStreamPageData)

    const seoFilters: SelectedLooseFilterMapType = {
      term: [seoStreamPageData.searchTerm],
    }
    if (seoStreamPageData.brandIds.length) {
      seoFilters.brand = seoStreamPageData.brandIds.map(id => id.toString())
    }

    const rootId = store.layoutStore.getMappedCategoryId(
      'root',
      getValidGender(seoStreamPageData.gender),
    ) as string
    const currentCategoryId = seoStreamPageData.categoryId?.toString() || rootId
    const currentCategory = await service.categories.requestByCategoryId(
      currentCategoryId,
      {
        strategy: 'cache_or_network',
      },
    )

    if (currentPage > ELASTIC_SEARCH_MAXIMUM_PAGE || currentPage < 0) {
      throw redirect(
        pathname,
        { page: undefined },
        {
          replaceState: true,
        },
      )
    }

    store.searchPage.filter.updateSelected(seoFilters)

    const availableFilterRequest: Promise<FilterItem[]> = (() => {
      return service.filters
        .requestByCategoryId(
          currentCategoryId,
          {
            selectedFilters: seoFilters,
          },
          { strategy: 'cache_or_network' },
        )
        .catch(() => [])
    })()

    const currentBatch = PaginationViewStore.pageToFirstBatch(currentPage)
    const sortChannel = getSortChannel({
      selectedFilters: seoFilters,
      sortId,
    })

    const fetchProducts = createProductStreamFetcher({
      categoryId: currentCategoryId,
      filter: seoFilters,
      score: undefined,
      service,
      sortId,
      sortChannel,
    })

    const [{ pagination, productIds }] = await Promise.all([
      fetchProducts({
        perPage: PaginationViewStore.productsPerBatch,
        page: currentBatch,
      }),
      store.layoutStore.fetchNavigation(),
    ])

    if (currentPage > pagination.last) {
      throw redirect(
        pathname,
        { page: undefined },
        {
          replaceState: true,
        },
      )
    }

    const availableFilters = await availableFilterRequest
    const finalAvailableFilters =
      await getAvailableFiltersWithAvailableAndSelectedValues(
        availableFilters,
        seoFilters,
        {
          loadAttributesByFilterNames:
            service.filters.requestAttributesByFilterNames,
        },
      )

    if (productIds.length === 0) {
      transaction(() => {
        store.searchPage.reset({
          productIds: [],
        })
        store.searchPage.filter.reset({
          categoryId: currentCategoryId,
          appliedSelectedLooseFilters: seoFilters,
        })
        store.searchPage.pagination.reset({
          currentPage,
          total: 0,
          totalPages: 0,
        })
        store.searchPage.category.reset(currentCategory)
        store.searchPage.endLoading()
      })
      return
    }

    transaction(() => {
      store.searchPage.reset({
        productIds,
      })
      store.searchPage.filter.reset({
        availableFilters: finalAvailableFilters,
        selectedFilters: seoFilters,
        appliedSelectedLooseFilters: seoFilters,
      })
      store.searchPage.category.reset(currentCategory)
      store.searchPage.pagination.reset({
        currentPage,
        total: pagination.total,
        totalPages: PaginationViewStore.totalBatchesToTotalPages(
          pagination.last,
        ),
      })
      store.searchPage.sorting.reset({
        appliedProductsSortingId: sortId,
        sortChannel,
      })

      store.searchPage.endLoading()
    })
  },
}
